import { useCallback, useEffect, useState } from 'react';
import { useReleases } from './useVersionHistory';
import { releaseKey } from './whatsNewTypes';

const STORAGE_KEY = 'sorigin.whatsNew.lastSeen';

/**
 * Tracks whether the current user has already opened the newest update.
 *
 * The "unseen" marker on the sidebar link is the only attention the section
 * ever asks for — a plain dot plus the word "new", cleared the moment the
 * history is opened. A draft release (no confirmed date) never raises the
 * marker, so nothing shouts before the team has confirmed it.
 */
export function useWhatsNew() {
  const { data } = useReleases();
  const latest = data && data.length > 0 ? data[0] : undefined;

  const currentKey = latest ? releaseKey(latest) : null;
  const isDraft = !!latest?.draft;

  const [lastSeen, setLastSeen] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Keep in sync if another tab marks it seen.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setLastSeen(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const hasUnseen = !!latest && !isDraft && lastSeen !== currentKey;

  const markSeen = useCallback(() => {
    if (!currentKey) return;
    try {
      localStorage.setItem(STORAGE_KEY, currentKey);
    } catch {
      /* ignore storage failures — the marker simply stays until next load */
    }
    setLastSeen(currentKey);
  }, [currentKey]);

  return { hasUnseen, markSeen, latestRelease: latest };
}
